import { SchmancyFormField } from '@schmancy/mixins/formField.mixin'
import { css, html, nothing, PropertyValues } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { fullWidth } from '@schmancy/directives/layout'
// The single-row mode renders <schmancy-scroll>; unregistered it is an inert
// unknown element with no overflow, so the row absorbs nothing and spills.
import '@schmancy/primitives/layout/scroll/scroll'
import type { FilterChipChangeEvent as SchmancyChipChangeEvent } from './filter-chip'
import { SchmancyFilterChip as SchmancyChip } from './filter-chip'

export type SchmancyChipsChangeEvent = CustomEvent<{ value: string | string[] }>

/**
 * One Tailwind inline-axis padding utility, or `''` for none. `pl-*` / `pr-*`
 * are deliberately unspellable here: Tailwind emits them as `padding-left` /
 * `padding-right`, which pins the breathing room to the left of an RTL row.
 * `px-*` is `padding-inline`, `ps-*` / `pe-*` are its start/end halves.
 */
export type SchmancyChipsInlinePadding = '' | `px-${string}` | `ps-${string}` | `pe-${string}`

@customElement('schmancy-chips')
export default class SchmancyChips extends SchmancyFormField(css`
	:host {
		display: block;
		height: fit-content;
		width: fit-content;
		/* The inner scroller absorbs the row's overflow, so the host declines both
		   routes by which that overflow otherwise travels outward: min-width waives
		   the automatic minimum a grid or flex item carries, max-width caps the
		   fit-content default, which resolves to the row's own min-content.
		   Measured in chips.test.ts: 300px container, 414px before, 300px after. */
		min-width: 0;
		max-width: 100%;
	}
	/* :host's own width above loses to a consumer's w-full on the host: the cascade
	   sorts encapsulation context BEFORE layers, and for normal declarations the outer
	   tree wins, so a .w-full in the consumer's sheet beats this inner-tree :host rule
	   whatever its layer or specificity. width='full' sets the same 100% through the
	   component's own vocabulary — pick one and use it consistently, because a row
	   carrying neither hugs its content while a sibling carrying either stretches.
	   Measured in chips.test.ts: 400px container, 400px with w-full, 93px without. */
	:host([width='full']) {
		width: 100%;
	}
`) {
	// FACE wiring (formAssociated, internals, attachInternals) comes from
	// SchmancyFormField. Same for: name, required, disabled, validationMessage,
	// validateOn, touched/dirty/pristine/submitted, markTouched/markSubmitted,
	// formResetCallback, formDisabledCallback, FIELD_CONNECT_EVENT dispatch,
	// emitChange(), checkValidity(), reportValidity(), setCustomValidity().

	@property({ type: Boolean, reflect: true })
	multi: boolean = false

	@property({ attribute: 'aria-label', reflect: true })
	override ariaLabel: string | null = null

	/** `compact` sets every chip's `density`, for a row of scopes in a tool. See `schmancy-filter-chip`. */
	@property({ reflect: true })
	density?: 'compact'

	/** 'auto' (default) hugs its content. 'full' stretches to the width its own layout container allows — the component's own spelling of a `w-full` class on the host, which reaches the same 100% (see the `:host([width='full'])` comment above). */
	@property({ reflect: true, type: String })
	width: 'auto' | 'full' = 'auto'

	// Override `value` with the chips-specific type and a custom getter/setter
	// pair backed by reactive subjects.
	@property({ reflect: false })
	override get value(): string | string[] {
		return this.multi ? (this.values$?.value ?? []) : (this.value$?.value ?? '')
	}
	override set value(v: string | string[]) {
		const old = this.value
		if (this.multi) {
			const arr = Array.isArray(v)
				? v
				: v
					? String(v)
							.split(',')
							.map(s => s.trim())
							.filter(Boolean)
					: []
			this.values$?.next(arr)
		} else {
			this.value$?.next(Array.isArray(v) ? (v[0] ?? '') : String(v ?? ''))
		}
		this.requestUpdate('value', old)
	}

	/** Typed convenience alias for multi-mode consumers. */
	get values(): string[] {
		return [...(this.values$?.value ?? [])]
	}
	set values(v: string[]) {
		this.values$?.next(Array.isArray(v) ? [...v] : [])
	}

	/**
	 * Inline-axis padding held INSIDE the row — in single-row mode, inside its
	 * own scroller. A scrollport is the padding box, so the row still reaches
	 * the container's real edges and the chips scroll through this space; the
	 * first and last simply rest inside it. The same utility on an ancestor
	 * instead shrinks the scroll viewport, cutting the row at an inset edge,
	 * which is why a full-bleed chip row cannot be padded from outside at all.
	 *
	 * `wrap` mode has no scroller and takes it identically: the promise — this
	 * much space before the first chip and after the last — reads the same
	 * there, so a row that flips `wrap` at a breakpoint keeps its inset.
	 */
	// Not reflected: the default is '', and reflecting that would stamp an empty
	// inline-padding="" onto every chips element already in the tree.
	@property({ attribute: 'inline-padding' })
	inlinePadding: SchmancyChipsInlinePadding = ''

	@property({ type: Boolean, reflect: true })
	wrap: boolean = false

	@property({ type: String, reflect: true })
	justify: 'start' | 'center' | 'end' = 'start'

	/**
	 * Who owns the selection. Unset, the group does: a press moves `value` and
	 * then says so. Set, the CONSUMER does: a press dispatches `change` carrying
	 * the value the user asked for and moves nothing, so the bound `value` is the
	 * one thing that selects a chip — the same contract as
	 * `<schmancy-switch controlled>`, for a press that commits a save.
	 */
	@property({ type: Boolean, reflect: true })
	controlled: boolean = false

	/**
	 * The save a press started is in flight. The pressed chip — or the selected
	 * one, when nothing was pressed — shows it in place with a spinner and no
	 * dim, every chip keeps its tab stop, and every press is ignored until it
	 * clears. `aria-busy` rides the group. `disabled` wins over it.
	 */
	@property({ type: Boolean, reflect: true })
	busy: boolean = false

	/** The chip whose press is the one in flight while `busy`. */
	#pressed: Element | undefined

	@queryAssignedElements({
		selector:
			'schmancy-chip, schmancy-filter-chip, schmancy-assist-chip, schmancy-input-chip, schmancy-suggestion-chip',
		flatten: true,
	})
	chips!: (SchmancyChip | HTMLElement)[]

	private value$ = new BehaviorSubject<string>('')
	private values$ = new BehaviorSubject<string[]>([])

	/** The chips THIS group disabled, so re-enabling the group clears exactly
	 *  those and never a chip the consumer disabled on its own — an option a
	 *  consumer marks unavailable (a past date, an out-of-stock size) stays
	 *  unavailable, which an unconditional write of the group's own `disabled`
	 *  silently undid on every selection change. */
	#groupDisabled = new WeakSet<Element>()

	/** The chips THIS group put in readonly, on the same terms as
	 *  `#groupDisabled` — a chip a consumer marked readonly on its own stays
	 *  readonly when the group leaves that state. */
	#groupReadonly = new WeakSet<Element>()

	protected override willUpdate(changedProps: PropertyValues): void {
		super.willUpdate(changedProps)
		// super handles the string path; fix the array path for multi mode.
		if (changedProps.has('value') && Array.isArray(this.value) && this.name) {
			const fd = new FormData()
			this.value.forEach(v => fd.append(this.name, v))
			this.internals?.setFormValue(fd)
		}
		// Re-forward `disabled` to every managed chip on later toggles too, not
		// only at connect/firstUpdated — see updateChipStates' header comment.
		if (changedProps.has('disabled') || changedProps.has('readonly') || changedProps.has('busy'))
			this.updateChipStates(this.value$.value, this.values$.value)
		if (changedProps.has('busy') || changedProps.has('disabled')) {
			if (this.busy && !this.disabled) this.setAttribute('aria-busy', 'true')
			else this.removeAttribute('aria-busy')
		}
		// The save a press started has answered; the next busy belongs to the next press.
		if (changedProps.has('busy') && !this.busy) this.#pressed = undefined
	}

	override checkValidity(): boolean {
		if (this.required && Array.isArray(this.value) && this.value.length === 0) {
			return this.setFieldValidity({ valueMissing: true }, this.errorMessages?.valueMissing ?? 'This field is required')
		}
		return super.checkValidity()
	}

	/**
	 * The host holds no tab stop — each chip is its own — so focusing it, as a
	 * refused submit or a summary link does, would land nowhere. It forwards to
	 * the chip already chosen, else the first one on offer, which `delegatesFocus`
	 * cannot express: it always takes the first focusable.
	 */
	override focus(options?: FocusOptions): void {
		const enabled = this.chips.filter(chip => !('disabled' in chip && chip.disabled === true))
		const target = enabled.find(chip => 'selected' in chip && chip.selected === true) ?? enabled[0]
		if (target) target.focus(options)
		else super.focus(options)
	}

	override connectedCallback() {
		super.connectedCallback()

		// Sync BehaviorSubjects with whatever value was set before connection.
		if (this.multi) {
			const cur = this.value
			this.values$.next(Array.isArray(cur) ? cur : [])
		} else {
			const cur = this.value
			this.value$.next(Array.isArray(cur) ? (cur[0] ?? '') : String(cur ?? ''))
		}

		combineLatest([
			this.value$.pipe(distinctUntilChanged()),
			this.values$.pipe(
				distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((v, i) => v === curr[i])),
			),
		])
			.pipe(debounceTime(0), takeUntil(this.disconnecting))
			.subscribe(([value, values]) => {
				this.updateChipStates(value, values)
			})
	}

	private updateChipStates(value: string, values: string[]) {
		if (!this.chips) return

		this.chips.forEach(chip => {
			// The group's own `disabled` disables every managed chip — without
			// this a disabled <schmancy-chips> left each child chip's own
			// `disabled` at its unrelated default (false), so the chip's real
			// click-blocking guard (handleClick/handleKeyDown checking
			// `this.disabled`) never engaged and a click still dispatched
			// `change` past a "disabled" chip group. It disables, and it
			// UN-disables only what it disabled: a per-chip `?disabled=` the
			// consumer set is the consumer's, and forcing it back to the group's
			// own value on every selection change made a per-option disabled
			// state impossible to express at all.
			if ('disabled' in chip) {
				const c = chip as unknown as { disabled: boolean }
				if (this.disabled) {
					if (!c.disabled) this.#groupDisabled.add(chip)
					c.disabled = true
				} else if (this.#groupDisabled.delete(chip)) {
					c.disabled = false
				}
			}

			// `readonly` rides the same forwarding for the same reason: the guard
			// that actually blocks a chip's activation reads the CHIP's own
			// readonly, so a group left holding the word alone is a group whose
			// chips still dispatch. Its own display differs from disabled's —
			// each chip keeps its tab stop and announces `aria-disabled` instead
			// of leaving the tab order — and that is the chip's to say, not this
			// group's to paint.
			if ('readonly' in chip) {
				const c = chip as unknown as { readonly: boolean }
				if (this.readonly) {
					if (!c.readonly) this.#groupReadonly.add(chip)
					c.readonly = true
				} else if (this.#groupReadonly.delete(chip)) {
					c.readonly = false
				}
			}

			if ('value' in chip && 'selected' in chip) {
				const filterChip = chip as SchmancyChip
				filterChip.density = this.density
				if (this.multi) {
					filterChip.selected = values.length > 0 && values.includes(filterChip.value)
				} else {
					filterChip.selected = value !== '' && value === filterChip.value
				}
				// The press in flight, else the chip the selection already rests on.
				filterChip.busy =
					this.busy && !this.disabled && (this.#pressed ? chip === this.#pressed : filterChip.selected)
			}
		})
	}

	async change(e: CustomEvent<SchmancyChipChangeEvent>) {
		e.preventDefault()
		e.stopPropagation()

		// Defense-in-depth: a managed chip's own `disabled` / `readonly` / `busy`
		// (forwarded by updateChipStates) already blocks its click/keydown
		// activation, and a busy group's other chips still dispatch — so the group
		// never trusts a UI affordance alone to enforce any of the three.
		if (this.disabled || this.readonly || this.busy) return

		const { value: chipValue, selected } = e.detail
		// Required single-mode: ignore deselection attempt.
		if (!this.multi && !selected && this.required) return

		let next: string | string[]
		if (this.multi) {
			const held = this.values$.value
			next = selected ? (held.includes(chipValue) ? held : [...held, chipValue]) : held.filter(v => v !== chipValue)
		} else {
			next = selected ? chipValue : ''
		}
		// A press accepted is entry finished — a chip group has no mid-entry state
		// the way a typed field does, so it is touched on the act rather than on a
		// blur that a click straight to submit never produces.
		this.markTouched()
		this.#pressed = this.chips.find(chip => e.composedPath().includes(chip))

		// Controlled, the bound `value` is the only writer: a press asks and moves nothing.
		if (!this.controlled) {
			const old = this.value
			if (Array.isArray(next)) this.values$.next(next)
			else this.value$.next(next)
			this.requestUpdate('value', old)
		}

		this.emitChange({ value: next })
	}

	override resetForm(): void {
		super.resetForm()
		this.value$.next('')
		this.values$.next([])
	}

	protected firstUpdated(changedProperties: PropertyValues): void {
		super.firstUpdated(changedProperties)
		this.updateChipStates(this.value$.value, this.values$.value)
	}

	protected render(): unknown {
		const justifyClass =
			this.justify === 'center' ? 'justify-center' : this.justify === 'end' ? 'justify-end' : 'justify-start'
		const chipClasses = {
			flex: true,
			'gap-2': this.density !== 'compact',
			'gap-0.5': this.density === 'compact',
			'flex-wrap': this.wrap,
			'flex-nowrap': !this.wrap,
			[justifyClass]: true,
			// Rides the row container itself — the scroller in single-row mode —
			// never the wrapper below, whose padding would shrink the scrollport.
			// `gap-2` spaces chips from each OTHER and this pads the row's two ends,
			// so neither doubles the other at the first or last chip. An empty
			// string contributes no key (classMap splits and drops blanks).
			[this.inlinePadding]: !!this.inlinePadding,
		}
		const wrapperClasses = {
			'rounded-lg transition-colors duration-200': true,
			'outline-solid outline-2 outline-offset-4 outline-error-default': this.error,
		}
		// wrap mode flows chips in a natural-height container that grows to fit them; only the
		// single-row (non-wrap) mode needs a horizontal scroller. A scroller in wrap mode would
		// clamp height (schmancy-scroll's explicit-height fallback) and clip the wrapped rows.
		const chips = html`<slot
			@slotchange=${() => {
				this.updateChipStates(this.value$.value, this.values$.value)
			}}
		></slot>`
		return html` <div class="${this.classMap(wrapperClasses)}">
				${
					this.wrap
						? html`<div class="${this.classMap(chipClasses)}" ${fullWidth()} @change=${this.change}>${chips}</div>`
						: // The keyboard stop lives inside this shadow root, so the host's
							// accessible name must reach the scroll region too.
							html`<schmancy-scroll
								hide
								direction="horizontal"
								class="${this.classMap(chipClasses)}"
								label=${this.label || this.ariaLabel || ''}
								${fullWidth()}
								@change=${this.change}
							>
								${chips}
							</schmancy-scroll>`
				}
			</div>

			${when(
				this.hint || (this.error && this.validationMessage),
				() => html`
					<div
						id="hint-${this.id}"
						class=${this.classMap({ 'mt-1 text-sm wrap-anywhere': true, 'text-error-default': this.error, 'text-surface-onVariant': !this.error })}
						role=${ifDefined(this.error ? 'alert' : undefined)}
						aria-live="polite"
					>
						${this.error && this.validationMessage ? this.validationMessage : this.hint}
					</div>
				`,
				() => nothing,
			)}`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-chips': SchmancyChips
	}
}
